import PropTypes from "prop-types";
import React from "react";
import { useEffect, useState } from "react";
import QuotaLimitHeader from "./quota/QuotaLimitHeader";
import { fetchNotifications, URLs } from "../utils/apis";
import { useLocation } from "react-router-dom";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import {
  Anchor,
  Image,
  Menu,
  Text,
  Box,
  Flex,
  Container,
  Burger,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useStylesContainer } from "../utils/mantine/useStylesContainer";
import { useStylesHeader } from "../utils/mantine/useStylesHeader";
import CustomAnchorRoute from "./mantineCustomComponents/CustomAnchorRoute";
import { Link } from "react-router-dom";
import { InfoBanner } from "./InfoBanner";
import { useStylesInfoBanner } from "../utils/mantine/useStylesInfoBanner";

const Header = ({
  staticBasePath,
  djangoContext,
  changedUserName = false,
  updateStampQuota,
}) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(
    djangoContext.userIsAuthenticated || false,
  );
  const [email, setEmail] = useState(""); // eslint-disable-line no-unused-vars

  const [shortName, setShortName] = useState("");

  const [opened, { toggle, close }] = useDisclosure();

  //css Classes
  const { classes: containerClasses } = useStylesContainer();
  const { classes: headerClasses } = useStylesHeader();
  const { classes: classesInfoBanner } = useStylesInfoBanner();
  // for staging
  const [userProfileShow] = useState(true);

  useEffect(() => {
    const { userEmail, userShortName } = djangoContext;

    setEmail(userEmail || "");
    setShortName(userShortName || "");

    // Check authentication dynamically
    const checkAuth = async () => {
      const { checkAuthentication } = await import("../utils/apis");
      await checkAuthentication(setIsAuthenticated);
    };
    checkAuth();
  }, []);

  const currentPage = location.pathname; // Extract the pathname

  const shouldShowNotification = (notification) => {
    if (notification.page_type === "all_pages") {
      return true; // Show on all pages
    }
    if (notification.page_type === "stamp" && currentPage.includes("/stamp")) {
      return true; // Show only on the stamp page (or pages that include /stamp in the URL)
    }
    if (
      notification.page_type === "verify" &&
      currentPage.includes("/verify")
    ) {
      return true; // Show only on the verify page (or pages that include /verify in the URL)
    }
    return false;
  };

  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    fetchNotifications(setNotifications); // Now only passing setNotifications
  }, []);

  return (
    <>
      {notifications && notifications.length > 0
        ? notifications.map((notification) => {
            if (shouldShowNotification(notification)) {
              return (
                <Box
                  key={notification.id}
                  className={classesInfoBanner.bannerBox}
                >
                  <Container
                    className={`${containerClasses.container} relative`}
                  >
                    <InfoBanner
                      title={notification.title}
                      type={notification.type}
                      className={classesInfoBanner.banner}
                    >
                      {notification.message}
                    </InfoBanner>
                  </Container>
                </Box>
              );
            } else {
              return null; // Explicitly return null for notifications that don't match
            }
          })
        : null}

      <Box className={headerClasses.header}>
        <Container className={containerClasses.container}>
          <Box className="header-box">
            <Flex
              justify={theme.flex.justify.between}
              className="rb-header-desktop"
              align={theme.flex.align.center}
            >
              <Box>
                <Flex
                  align={theme.flex.align.center}
                  className={headerClasses.headerLeft}
                >
                  <Anchor href="/">
                    <Image
                      w="1.73em"
                      h="2.2em"
                      src={`${staticBasePath}public/images/svg/vb_logo.svg`}
                      alt="validityBase"
                    />
                  </Anchor>
                  <CustomAnchorRoute
                    componentClasses="link link_black nav-link"
                    text="Stamp"
                    href="/stamp/"
                  />
                  <CustomAnchorRoute
                    componentClasses="link link_black nav-link"
                    text="Verify"
                    href="/verify/"
                  />

                  <Menu shadow="md" width="15em">
                    <Menu.Target>
                      <Text className={headerClasses.menuButton}>▼</Text>
                    </Menu.Target>

                    <Menu.Dropdown className={headerClasses.menu}>
                      <Menu.Item
                        component="a"
                        className="nav-link"
                        href="https://vbase.com/"
                      >
                        Back to vbase.com
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        component="a"
                        className="nav-link"
                        href="https://github.com/validityBase/"
                      >
                        Code
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        component="a"
                        className="nav-link"
                        href="https://docs.vbase.com/"
                      >
                        Docs
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Flex>
              </Box>
              <Box>
                {isAuthenticated ? (
                  <Flex
                    align={theme.flex.align.center}
                    className={headerClasses.headerRight}
                  >
                    {userProfileShow && (
                      <>
                        {location.pathname !== URLs.PROFILE_PAYMENT_URL && (
                          <QuotaLimitHeader
                            updateStampQuota={updateStampQuota}
                          />
                        )}
                        <CustomAnchor
                          text="View Plans"
                          href={URLs.PROFILE_PAYMENT_URL}
                          componentClasses="buttonWhite buttonWhiteHeader buttonWhiteHeaderPlan"
                        />
                      </>
                    )}
                    <Menu shadow="md" width="15em">
                      <Menu.Target className={headerClasses.menuButtonUser}>
                        <Flex gap={theme.flex.gap.md}>
                          <Text component="span" className="user-menu-name">
                            {changedUserName ? (
                              <>{changedUserName}</>
                            ) : (
                              <>{shortName}</>
                            )}
                          </Text>
                          <Text component="span" className="user-menu-arrow">
                            ▼
                          </Text>
                        </Flex>
                      </Menu.Target>

                      <Menu.Dropdown className={headerClasses.menu}>
                        <Menu.Item
                          component={Link}
                          to="/profile"
                          onClick={async (e) => {
                            const { checkAuthentication } = await import(
                              "../utils/apis"
                            );
                            const authStatus = await checkAuthentication(
                              setIsAuthenticated,
                            );
                            if (authStatus === false) {
                              e.preventDefault();
                              window.location.href = "/accounts/login/";
                            }
                          }}
                        >
                          User Profile
                        </Menu.Item>

                        <Menu.Item
                          component="a"
                          href="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-stamper"
                        >
                          How to use vBase Stamper
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          component="a"
                          className="logout-btn"
                          href="/accounts/logout/"
                        >
                          Log Out
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Flex>
                ) : (
                  <Flex gap={theme.flex.gap.sm}>
                    <CustomAnchor
                      text="Create an Account"
                      href="/accounts/signup/"
                      componentClasses="buttonBlack buttonBlackHeader"
                    />
                    <CustomAnchor
                      text="Sign In"
                      href="/accounts/login/"
                      componentClasses="buttonWhite buttonWhiteHeader"
                    />
                  </Flex>
                )}
              </Box>
            </Flex>
            <Box className="rb-header-mobile">
              <Flex gap={theme.flex.gap.xs} align={theme.flex.align.center}>
                <Anchor href="/">
                  <Image
                    w="1.2em"
                    h="1.535em"
                    src={`${staticBasePath}public/images/svg/vb_logo.svg`}
                    alt="validityBase"
                  />
                </Anchor>

                <Menu shadow="md" width="15em">
                  <Menu.Target>
                    <Burger
                      size="sm"
                      lineSize={2}
                      opened={opened}
                      onClick={toggle}
                      aria-label="Toggle navigation"
                    />
                  </Menu.Target>

                  <Menu.Dropdown className={headerClasses.menu}>
                    <Menu.Item
                      component="a"
                      className="nav-link"
                      href="/stamp/"
                    >
                      Stamp
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      component="a"
                      className="nav-link"
                      href="/verify/"
                    >
                      Verify
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      component="a"
                      className="nav-link"
                      href="https://vbase.com/"
                    >
                      Back to vbase.com
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      component="a"
                      className="nav-link"
                      href="https://github.com/validityBase/"
                    >
                      Code
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      component="a"
                      className="nav-link"
                      href="https://docs.vbase.com/"
                    >
                      Docs
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
              {isAuthenticated ? (
                <>
                  <CustomAnchor
                    text="View Plans"
                    href={URLs.PROFILE_PAYMENT_URL}
                    componentClasses="buttonWhite buttonWhiteHeader buttonWhiteHeaderPlan"
                  />
                  <Menu shadow="md" width="15em">
                    <Menu.Target className={headerClasses.menuButtonUser}>
                      <Flex gap={theme.flex.gap.md} onClick={close}>
                        <Text component="span" className="user-menu-name">
                          {changedUserName ? (
                            <>{changedUserName}</>
                          ) : (
                            <>{shortName}</>
                          )}
                        </Text>
                        <Text component="span" className="user-menu-arrow">
                          ▼
                        </Text>
                      </Flex>
                    </Menu.Target>

                    <Menu.Dropdown className={headerClasses.menu}>
                      <Menu.Item
                        component="a"
                        href="/profile"
                        onClick={async (e) => {
                          e.preventDefault();
                          const { checkAuthentication } = await import(
                            "../utils/apis"
                          );
                          const authStatus = await checkAuthentication(
                            setIsAuthenticated,
                          );
                          if (authStatus === false) {
                            window.location.href = "/accounts/login/";
                          } else {
                            window.location.href = "/profile";
                          }
                        }}
                      >
                        User Profile
                      </Menu.Item>

                      <Menu.Item
                        component="a"
                        href="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-stamper"
                      >
                        How to use vBase Stamper
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        component="a"
                        className="logout-btn"
                        href="/accounts/logout/"
                      >
                        Log Out
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              ) : (
                <Flex gap={theme.flex.gap.sm}>
                  <CustomAnchor
                    text="Create an Account"
                    href="/accounts/signup/"
                    componentClasses="buttonBlack buttonBlackHeaderMobile"
                  />
                  <CustomAnchor
                    text="Sign In"
                    href="/accounts/login/"
                    componentClasses="buttonWhite buttonWhiteHeaderMobile"
                  />
                </Flex>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

Header.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  djangoContext: PropTypes.object.isRequired,
  currentScreen: PropTypes.string,
  changedUserName: PropTypes.bool,
  updateStampQuota: PropTypes.bool,
};
export default Header;
